import React, { useEffect, useRef, useState } from "react";
import { ABtn, AResult } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

const B = 320;                 // board size
const R = 8;                   // coin radius
const SR = 10;                 // striker radius
const PAD = 24;                // inner wall boundary
const POCKET = 18;             // pocket catch radius
const FRICTION = 0.985;
const STOP = 0.05;
const BASE_Y = B - 40;         // striker baseline y
// pockets sit at the inner corners (inside the wall) so coins actually fall in
const POCKETS = [[PAD + 8, PAD + 8], [B - PAD - 8, PAD + 8], [PAD + 8, B - PAD - 8], [B - PAD - 8, B - PAD - 8]];

const makeCoins = () => {
  const coins = [];
  const cx = B / 2, cy = B / 2;
  coins.push({ x: cx, y: cy, vx: 0, vy: 0, r: R, kind: "queen", live: true });
  // ring of 6
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i;
    coins.push({ x: cx + Math.cos(a) * (R * 2.1), y: cy + Math.sin(a) * (R * 2.1), vx: 0, vy: 0, r: R, kind: i % 2 ? "white" : "black", live: true });
  }
  // ring of 12
  for (let i = 0; i < 12; i++) {
    const a = (Math.PI / 6) * i + 0.26;
    coins.push({ x: cx + Math.cos(a) * (R * 4.15), y: cy + Math.sin(a) * (R * 4.15), vx: 0, vy: 0, r: R, kind: i % 2 ? "black" : "white", live: true });
  }
  return coins;
};

export default function Carrom() {
  const canvasRef = useRef(null);
  const st = useRef(null);
  const rafRef = useRef(null);
  const [turn, setTurn] = useState(0);           // 0 = you (white), 1 = cpu (black)
  const [counts, setCounts] = useState({ white: 0, black: 0 });
  const countsRef = useRef({ white: 0, black: 0 });
  const [msg, setMsg] = useState("Drag from the striker to aim, release to shoot");
  const [winner, setWinner] = useState(null);
  const [aiming, setAiming] = useState(false);
  const turnRef = useRef(0);

  useEffect(() => { turnRef.current = turn; }, [turn]);

  const reset = () => {
    st.current = { coins: makeCoins(), striker: { x: B / 2, y: BASE_Y, vx: 0, vy: 0, r: SR, live: true }, aim: null, moving: false, potted: [] };
    setCounts({ white: 0, black: 0 }); countsRef.current = { white: 0, black: 0 }; setWinner(null); setTurn(0); turnRef.current = 0;
    setMsg("Your turn — drag from the striker to aim");
    draw();
  };

  useEffect(() => { reset(); return () => cancelAnimationFrame(rafRef.current); }, []); // eslint-disable-line

  const draw = () => {
    const ctx = canvasRef.current.getContext("2d");
    const s = st.current; if (!s) return;
    ctx.fillStyle = "#e7b96a"; ctx.fillRect(0, 0, B, B);
    ctx.strokeStyle = "#8a5a2b"; ctx.lineWidth = 10; ctx.strokeRect(5, 5, B - 10, B - 10);
    ctx.strokeStyle = "#6b3f1d"; ctx.lineWidth = 2; ctx.strokeRect(24, 24, B - 48, B - 48);
    // center circle
    ctx.strokeStyle = "#b3141f"; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(B / 2, B / 2, 34, 0, 7); ctx.stroke();
    // baselines
    ctx.strokeStyle = "#b3141f"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(70, BASE_Y); ctx.lineTo(B - 70, BASE_Y); ctx.stroke();
    // pockets
    POCKETS.forEach(([x, y]) => { ctx.fillStyle = "#20130a"; ctx.beginPath(); ctx.arc(x, y, POCKET, 0, 7); ctx.fill(); ctx.strokeStyle = "#000"; ctx.lineWidth = 2; ctx.stroke(); });
    // coins
    s.coins.forEach((c) => {
      if (!c.live) return;
      ctx.fillStyle = c.kind === "white" ? "#fff7e6" : c.kind === "black" ? "#241a12" : "#c1121f";
      ctx.beginPath(); ctx.arc(c.x, c.y, c.r, 0, 7); ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,.35)"; ctx.lineWidth = 1.5; ctx.stroke();
    });
    // striker
    if (s.striker.live) {
      ctx.fillStyle = "#dfe7ef"; ctx.beginPath(); ctx.arc(s.striker.x, s.striker.y, s.striker.r, 0, 7); ctx.fill();
      ctx.strokeStyle = "#457b9d"; ctx.lineWidth = 3; ctx.stroke();
    }
    // aim line + predicted shot direction
    if (s.aim) {
      ctx.strokeStyle = "rgba(29,53,87,.85)"; ctx.lineWidth = 2; ctx.setLineDash([5, 5]);
      ctx.beginPath(); ctx.moveTo(s.striker.x, s.striker.y); ctx.lineTo(s.aim.x, s.aim.y); ctx.stroke(); ctx.setLineDash([]);
      const dx = s.striker.x - s.aim.x, dy = s.striker.y - s.aim.y; const mag = Math.hypot(dx, dy) || 1;
      ctx.strokeStyle = "rgba(193,18,31,.75)"; ctx.lineWidth = 2.5; ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(s.striker.x, s.striker.y); ctx.lineTo(s.striker.x + (dx / mag) * 90, s.striker.y + (dy / mag) * 90); ctx.stroke(); ctx.setLineDash([]);
    }
  };

  const settleTurn = () => {
    const s = st.current;
    const potted = s.potted; s.potted = [];
    // count newly potted (authoritative via ref to avoid stale state on consecutive pots)
    const nc = { ...countsRef.current };
    let pottedStriker = false, gainedOwn = false;
    potted.forEach((k) => {
      if (k === "striker") pottedStriker = true;
      else if (k === "white") nc.white++;
      else if (k === "black") nc.black++;
    });
    const me = turnRef.current === 0 ? "white" : "black";
    if (potted.includes(me)) gainedOwn = true;
    countsRef.current = nc; setCounts(nc);

    // striker returns to baseline
    s.striker.x = B / 2; s.striker.y = BASE_Y; s.striker.vx = 0; s.striker.vy = 0; s.striker.live = true;

    if (nc.white >= 9) { setWinner(0); sfx.win(); setMsg("🎉 You potted all your coins — You Win!"); draw(); return; }
    if (nc.black >= 9) { setWinner(1); sfx.lose(); setMsg("CPU cleared the board 😜"); draw(); return; }

    let next = turnRef.current;
    if (pottedStriker) { setMsg("Foul! Striker potted — turn passes"); next = (turnRef.current + 1) % 2; }
    else if (gainedOwn) { setMsg(turnRef.current === 0 ? "Nice pot! Go again" : "CPU pots and goes again"); }
    else { next = (turnRef.current + 1) % 2; }
    setTurn(next); turnRef.current = next;
    draw();
    if (next === 0 && !pottedStriker && !gainedOwn) setMsg("Your turn — aim and shoot");
  };

  const simulate = () => {
    const s = st.current; s.moving = true;
    const bodies = () => [s.striker, ...s.coins].filter((b) => b.live);
    const loop = () => {
      const arr = bodies();
      arr.forEach((b) => { b.x += b.vx; b.y += b.vy; b.vx *= FRICTION; b.vy *= FRICTION; if (Math.abs(b.vx) < STOP) b.vx = 0; if (Math.abs(b.vy) < STOP) b.vy = 0; });
      // pockets FIRST (before walls) so a coin reaching a corner falls in instead of bouncing
      arr.forEach((b) => {
        if (!b.live) return;
        for (const [px, py] of POCKETS) {
          if (Math.hypot(b.x - px, b.y - py) < POCKET) { b.live = false; b.vx = 0; b.vy = 0; s.potted.push(b === s.striker ? "striker" : b.kind); sfx.eat(); break; }
        }
      });
      const live = arr.filter((b) => b.live);
      // walls
      live.forEach((b) => {
        if (b.x - b.r < PAD) { b.x = PAD + b.r; b.vx = -b.vx * 0.7; }
        if (b.x + b.r > B - PAD) { b.x = B - PAD - b.r; b.vx = -b.vx * 0.7; }
        if (b.y - b.r < PAD) { b.y = PAD + b.r; b.vy = -b.vy * 0.7; }
        if (b.y + b.r > B - PAD) { b.y = B - PAD - b.r; b.vy = -b.vy * 0.7; }
      });
      // collisions
      for (let i = 0; i < live.length; i++) for (let j = i + 1; j < live.length; j++) {
        const a = live[i], b = live[j];
        const dx = b.x - a.x, dy = b.y - a.y; const dist = Math.hypot(dx, dy) || 0.001; const min = a.r + b.r;
        if (dist < min) {
          const nx = dx / dist, ny = dy / dist; const overlap = (min - dist) / 2;
          a.x -= nx * overlap; a.y -= ny * overlap; b.x += nx * overlap; b.y += ny * overlap;
          const dvx = b.vx - a.vx, dvy = b.vy - a.vy; const rel = dvx * nx + dvy * ny;
          if (rel < 0) {
            const imp = rel; a.vx += imp * nx; a.vy += imp * ny; b.vx -= imp * nx; b.vy -= imp * ny;
            if (Math.abs(rel) > 1.5) sfx.click();
          }
        }
      }
      draw();
      const anyMoving = live.some((b) => b.vx !== 0 || b.vy !== 0);
      if (anyMoving) { rafRef.current = requestAnimationFrame(loop); }
      else { s.moving = false; settleTurn(); }
    };
    rafRef.current = requestAnimationFrame(loop);
  };

  const shoot = (dirx, diry, power) => {
    const s = st.current; if (s.moving) return;
    const mag = Math.hypot(dirx, diry) || 1;
    s.striker.vx = (dirx / mag) * power; s.striker.vy = (diry / mag) * power;
    sfx.shoot(); s.aim = null; setAiming(false); simulate();
  };

  // pointer aim (human)
  const toBoard = (e) => { const r = canvasRef.current.getBoundingClientRect(); return { x: (e.clientX - r.left) / r.width * B, y: (e.clientY - r.top) / r.height * B }; };
  const onDown = (e) => {
    const s = st.current; if (s.moving || turnRef.current !== 0 || winner !== null) return;
    const p = toBoard(e);
    if (Math.hypot(p.x - s.striker.x, p.y - s.striker.y) < 40) { setAiming(true); s.aim = p; draw(); }
  };
  const onMove = (e) => { const s = st.current; if (!aiming) return; s.aim = toBoard(e); draw(); };
  const onUp = (e) => {
    const s = st.current; if (!aiming) return;
    const p = toBoard(e);
    // pull back to shoot: direction from pointer toward striker
    const dx = s.striker.x - p.x, dy = s.striker.y - p.y;
    const power = Math.min(20, Math.hypot(dx, dy) / 6);
    if (power > 1.2) shoot(dx, dy, power); else { s.aim = null; setAiming(false); draw(); }
  };

  // CPU turn — aim so striker → coin → pocket are as colinear as possible
  useEffect(() => {
    if (winner === null && turn === 1) {
      const t = setTimeout(() => {
        const s = st.current; if (!s || s.moving) return;
        const blacks = s.coins.filter((c) => c.live && c.kind === "black");
        const pool = blacks.length ? blacks : s.coins.filter((c) => c.live);
        let bestC = null, bestErr = Infinity;
        for (const c of pool) {
          for (const [px, py] of POCKETS) {
            const aS = Math.atan2(c.y - s.striker.y, c.x - s.striker.x);
            const aP = Math.atan2(py - c.y, px - c.x);
            let d = Math.abs(aS - aP); if (d > Math.PI) d = 2 * Math.PI - d;
            if (d < bestErr) { bestErr = d; bestC = c; }
          }
        }
        const tgt = bestC || { x: B / 2, y: B / 2 };
        const nx = tgt.x - s.striker.x + (Math.random() * 8 - 4);
        const ny = tgt.y - s.striker.y + (Math.random() * 8 - 4);
        const dist = Math.hypot(nx, ny);
        setMsg("CPU is aiming…");
        shoot(nx, ny, Math.min(19, dist / 9 + 9));
      }, 900);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turn, winner]);

  return (
    <div className="w-full text-center">
      <div className="mb-2 flex items-center justify-center gap-2">
        <span data-testid="carrom-you" className={`flex items-center gap-1.5 rounded-md border-2 px-2.5 py-1 text-sm font-bold ${turn === 0 && winner === null ? "border-kirana-brown bg-kirana-gold" : "border-kirana-border bg-white"}`}>
          <span className="h-3 w-3 rounded-full border border-kirana-brown bg-[#fff7e6]" /> You: {counts.white}/9
        </span>
        <span data-testid="carrom-cpu" className={`flex items-center gap-1.5 rounded-md border-2 px-2.5 py-1 text-sm font-bold ${turn === 1 && winner === null ? "border-kirana-brown bg-kirana-gold" : "border-kirana-border bg-white"}`}>
          <span className="h-3 w-3 rounded-full bg-[#241a12]" /> CPU: {counts.black}/9
        </span>
      </div>
      <div className="relative mx-auto w-full" style={{ maxWidth: B }}>
        <canvas ref={canvasRef} width={B} height={B} data-testid="carrom-board"
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
          className="w-full touch-none rounded-md border-[3px] border-kirana-brown" style={{ touchAction: "none" }} />
        {winner !== null && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/55 p-3">
            <AResult win={winner === 0} text={winner === 0 ? "You Win! 🎯" : "CPU Wins 😜"} testId="carrom-over" />
            <ABtn tone="green" onClick={reset} className="mt-3" data-testid="carrom-again">Play Again</ABtn>
          </div>
        )}
      </div>
      <p className="mt-2 min-h-[20px] text-sm font-bold text-kirana-brown">{msg}</p>
      <p className="text-xs text-kirana-brown/60">Pull back from the striker (like a slingshot) and release to strike. Pot all your white coins.</p>
    </div>
  );
}
